import { useLocation, useNavigate } from "react-router-dom";
import React, { memo, useEffect, useState, useCallback } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Bloques plegables del menú. Un bloque sin `label` (el de inicio) no se
// pliega. El que contiene la sección abierta se despliega aunque arranque
// plegado, para que nunca quede escondido dónde estás.
const NavGroups = ({ navGroups, activeRoute, onItemClick, itemClass, activeItemClass }) => {
    const [openGroups, setOpenGroups] = useState(() => Object.fromEntries(
        navGroups.map((group) => [group.id, !group.collapsed]),
    ));
    const isOpen = (group) => !group.label || openGroups[group.id]
        || group.items.some((item) => item.to === activeRoute);

    return navGroups.map((group) => (
        <div key={group.id} className="flex flex-col space-y-1">
            {group.label && (
                <button
                    type="button"
                    className="flex items-center justify-between px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-base-content/50 hover:text-base-content"
                    onClick={() => setOpenGroups((prev) => ({ ...prev, [group.id]: !isOpen(group) }))}
                    aria-expanded={isOpen(group)}
                >
                    {group.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen(group) ? "" : "-rotate-90"}`} />
                </button>
            )}
            {isOpen(group) && group.items.map(({ to, icon: Icon, label }) => (
                <div
                    key={to}
                    className={to === activeRoute ? activeItemClass : itemClass}
                    onClick={(e) => onItemClick(to, e)}
                    style={{ cursor: 'pointer' }}
                    aria-current={to === activeRoute ? "page" : undefined}
                >
                    <Icon className="w-5 h-5 mr-3 inline"/>
                    <span className="text-sm">{label}</span>
                </div>
            ))}
        </div>
    ));
};

const Menu2Simple = memo(({
                              stateEditor,
                              isMobile,
                              mobileMenuOpen,
                              handleMenuToggle,
                              handleMenuItemClick,
                              mobileMenuRef,
                              navGroups,
                              onRouteChangeStart, // Nueva prop para comunicar con AdminRoutes
                              onRouteChangeComplete // Nueva prop para comunicar con AdminRoutes
                          }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const activeRoute = location.pathname.split('/').filter(Boolean).pop() || 'general';
    const [isClosing, setIsClosing] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);

    const activeClass = "bg-neutral text-base-300 font-bold p-3 rounded-lg transition-colors duration-200";
    const inactiveClass = "text-neutral hover:bg-secondary hover:text-white p-3 rounded-lg transition-colors duration-200";

    // Manejar navegación después de que termine la animación del contenido
    useEffect(() => {
        if (pendingNavigation && !mobileMenuOpen && !isClosing) {
            console.log('Menu closed, starting route change animation...');
            // Notificar a AdminRoutes que inicie su animación de salida
            if (onRouteChangeStart) {
                onRouteChangeStart(pendingNavigation);
            }

            // Esperar a que AdminRoutes termine su animación de salida (0.4s)
            setTimeout(() => {
                console.log('Executing navigation to:', pendingNavigation);
                navigate(pendingNavigation);
                setPendingNavigation(null);

                // Notificar que la navegación está completa
                if (onRouteChangeComplete) {
                    onRouteChangeComplete();
                }
            }, 400); // Esperar 0.4s para que termine la animación de AdminRoutes
        }
    }, [pendingNavigation, mobileMenuOpen, isClosing, navigate, onRouteChangeStart, onRouteChangeComplete]);

    const handleMenuItemClickWithAnimation = useCallback((to, event) => {
        event.preventDefault(); // Prevenir navegación inmediata

        if (mobileMenuOpen && !isClosing) {
            console.log('Starting menu close animation for route:', to);
            setIsClosing(true);
            setPendingNavigation(to);
            // Iniciar cierre del menú
            handleMenuItemClick();
        } else {
            // Si el menú ya está cerrado, manejar navegación directa
            if (onRouteChangeStart) {
                onRouteChangeStart(to);
            }

            setTimeout(() => {
                navigate(to);
                if (onRouteChangeComplete) {
                    onRouteChangeComplete();
                }
            }, 400);
        }
    }, [mobileMenuOpen, isClosing, handleMenuItemClick, onRouteChangeStart, onRouteChangeComplete, navigate]);

    const handleExitComplete = useCallback(() => {
        console.log('Menu exit animation completed');
        setIsClosing(false);
    }, []);

    console.log("Menu2Simple render - mobileMenuOpen:", mobileMenuOpen);

    return (
        <>
            {/* Móvil: menú desplegable */}
            {!stateEditor?.editor && isMobile && (
                <div className="w-full h-auto flex-none" ref={mobileMenuRef}>
                    <button
                        onClick={handleMenuToggle}
                        className="w-full flex justify-between items-center glassCard text-white p-3 rounded-lg mb-2"
                        aria-expanded={mobileMenuOpen}
                        aria-controls="mobile-navigation"
                        disabled={isClosing}
                    >
                        <span>Menú</span>
                        <motion.div
                            animate={{ rotate: mobileMenuOpen ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
                        </motion.div>
                    </button>

                    {/* AnimatePresence que depende del estado del padre */}
                    <AnimatePresence mode="wait" onExitComplete={handleExitComplete}>
                        {mobileMenuOpen && (
                            <motion.div
                                key="mobile-menu-stable"
                                initial={{ height: 0, opacity: 0, scale: 0.95 }}
                                animate={{ height: 'auto', opacity: 1, scale: 1 }}
                                exit={{ height: 0, opacity: 0, scale: 0.95 }}
                                transition={{
                                    duration: 0.3,
                                    ease: "easeOut"
                                }}
                                className="overflow-hidden rounded-lg glassCard origin-top"
                                id="mobile-navigation"
                            >
                                <motion.nav
                                    initial={{ y: -10, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: -10, opacity: 0 }}
                                    transition={{ delay: 0.1, duration: 0.2 }}
                                    className="flex flex-col space-y-2 p-2 max-h-[60vh] overflow-y-auto"
                                >
                                    <NavGroups
                                        navGroups={navGroups}
                                        activeRoute={activeRoute}
                                        onItemClick={handleMenuItemClickWithAnimation}
                                        itemClass={inactiveClass}
                                        activeItemClass={activeClass}
                                    />
                                </motion.nav>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}

            {/* Escritorio: menú fijo */}
            {!stateEditor?.editor && !isMobile && (
                <aside className="w-2/12 glassCard rounded-lg p-1">
                    <nav className="flex flex-col space-y-2 overflow-y-auto max-h-full p-1">
                        <NavGroups
                            navGroups={navGroups}
                            activeRoute={activeRoute}
                            onItemClick={handleMenuItemClickWithAnimation}
                            itemClass={inactiveClass}
                            activeItemClass={activeClass}
                        />
                    </nav>
                </aside>
            )}
        </>
    );
}, (prevProps, nextProps) => {
    // Comparación para evitar re-renders innecesarios
    return (
        prevProps.stateEditor?.editor === nextProps.stateEditor?.editor &&
        prevProps.isMobile === nextProps.isMobile &&
        prevProps.mobileMenuOpen === nextProps.mobileMenuOpen &&
        prevProps.navGroups === nextProps.navGroups
    );
});

Menu2Simple.displayName = 'Menu2Simple';

export default Menu2Simple;